import * as React from "react";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogFooterHint,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// ---------------------------------------------------------------------------
// Media query hook — co-located, no public API surface until a second consumer
// appears. SSR guard is defensive (fc-web-app is Vite/CSR-only).
// ---------------------------------------------------------------------------
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches,
  );
  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

// ---------------------------------------------------------------------------
// Internal context — lets sub-components know which primitive to render and
// whether the root is dismissible. Outside-click is always blocked by
// DialogContent/DrawerContent; `dismissible: false` additionally blocks ESC
// (dialog mode) and the swipe gesture (drawer mode, via the vaul root).
// ---------------------------------------------------------------------------
interface ResponsiveDialogContextValue {
  /** `drawer` = vaul (móvil) · `dialog` = modal centrada · `sheet` = panel lateral. */
  mode: "drawer" | "dialog" | "sheet";
  dismissible: boolean;
}
const ResponsiveDialogModeContext = React.createContext<ResponsiveDialogContextValue>(
  { mode: "dialog", dismissible: true },
);

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
export interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Consulted only in mobile (Drawer) mode. Default: "bottom". */
  side?: "bottom" | "right";
  /** Default: true. When false, ESC (dialog mode) and swipe-to-close (drawer
   *  mode) are also blocked. Outside-click never closes the dialog/drawer
   *  regardless of this flag. */
  dismissible?: boolean;
  /**
   * Forwarded to Radix Dialog.Root on desktop.
   * When false, disables the focus trap — useful when a guided tour's popover
   * renders outside the dialog DOM and needs to receive click events.
   * Has no effect in drawer (mobile) mode.
   * Default: true (standard Radix behaviour).
   */
  modal?: boolean;
  /**
   * Qué se dibuja en escritorio. `modal` es una modal centrada de 520px;
   * `drawer` es el panel lateral de 560px (DS 4.8).
   *
   * La regla del producto (DESIGN-SYSTEM.md 4.8bis) NO se decide por el tamaño
   * del formulario: crear o editar un registro va siempre en panel lateral,
   * la modal centrada queda para decisiones cortas que interrumpen. Por eso
   * los llamadores de creación/edición usan `<FormDrawer>`
   * (`components/ds/FormDrawer`) y no esta prop a mano.
   *
   * En móvil no cambia nada: siempre es el drawer inferior de vaul.
   * Default: "modal".
   */
  desktop?: "modal" | "drawer";
  children: React.ReactNode;
}

export function ResponsiveDialog({
  open,
  onOpenChange,
  side = "bottom",
  dismissible = true,
  modal,
  desktop = "modal",
  children,
}: ResponsiveDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const mode = !isDesktop ? "drawer" : desktop === "drawer" ? "sheet" : "dialog";

  if (mode === "drawer") {
    return (
      <ResponsiveDialogModeContext.Provider value={{ mode: "drawer", dismissible }}>
        <Drawer
          open={open}
          onOpenChange={onOpenChange}
          direction={side}
          dismissible={dismissible}
        >
          {children}
        </Drawer>
      </ResponsiveDialogModeContext.Provider>
    );
  }

  if (mode === "sheet") {
    return (
      <ResponsiveDialogModeContext.Provider value={{ mode: "sheet", dismissible }}>
        <Sheet open={open} onOpenChange={onOpenChange} modal={modal}>
          {children}
        </Sheet>
      </ResponsiveDialogModeContext.Provider>
    );
  }

  return (
    <ResponsiveDialogModeContext.Provider value={{ mode: "dialog", dismissible }}>
      <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
        {children}
      </Dialog>
    </ResponsiveDialogModeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export function ResponsiveDialogTrigger({
  ...props
}: React.ComponentProps<typeof DrawerTrigger>) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerTrigger {...props} />;
  if (mode === "sheet") return <SheetTrigger {...props} />;
  return <DialogTrigger {...props} />;
}

export interface ResponsiveDialogContentProps
  extends React.ComponentProps<"div"> {
  className?: string;
}

export function ResponsiveDialogContent({
  className,
  children,
  ...props
}: ResponsiveDialogContentProps) {
  const ctx = React.useContext(ResponsiveDialogModeContext);

  if (ctx.mode === "drawer") {
    return (
      <DrawerContent className={className} {...props}>
        {children}
      </DrawerContent>
    );
  }

  if (ctx.mode === "sheet") {
    return (
      <SheetContent
        className={className}
        onEscapeKeyDown={ctx.dismissible ? undefined : (e) => e.preventDefault()}
        {...props}
      >
        {children}
      </SheetContent>
    );
  }

  // Outside-click / interact-outside blocking lives in DialogContent (global
  // default). This also covers modal={false} tours: driver.js's popover
  // stealing focus no longer closes the dialog. ESC stays enabled except when
  // the root is non-dismissible (guided tours, non-skippable steps).
  return (
    <DialogContent
      className={className}
      onEscapeKeyDown={
        ctx.dismissible ? undefined : (e) => e.preventDefault()
      }
      {...props}
    >
      {children}
    </DialogContent>
  );
}

export function ResponsiveDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerHeader className={className} {...props} />;
  if (mode === "sheet") return <SheetHeader className={className} {...props} />;
  return <DialogHeader className={className} {...props} />;
}

export function ResponsiveDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerTitle>) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerTitle className={className} {...props} />;
  if (mode === "sheet") return <SheetTitle className={className} {...props} />;
  return <DialogTitle className={className} {...props} />;
}

export function ResponsiveDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerDescription>) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerDescription className={className} {...props} />;
  if (mode === "sheet") return <SheetDescription className={className} {...props} />;
  return <DialogDescription className={className} {...props} />;
}

/**
 * Clases del cuerpo, para cuando el contenedor scrolleable NO puede ser un div
 * propio — el caso de los formularios, donde el `<form>` tiene que envolver
 * campos Y pie para que el botón de submit siga asociado al formulario.
 *
 * `md:` es exactamente el breakpoint donde `ResponsiveDialog` cambia de drawer
 * a modal (`useMediaQuery("(min-width: 768px)")`), así que estas clases dan el
 * padding del drawer abajo y el de la modal arriba, sin ramificar en JS.
 *
 * MANTENER EN SINCRONÍA con `DialogBody`/`DrawerBody`. Cuando el cuerpo sí
 * puede ser su propio elemento, usar `<ResponsiveDialogBody>` en vez de esto.
 */
export const responsiveDialogBodyClass =
  "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:gap-[18px] md:p-[18px]";

/**
 * Gemelo de `responsiveDialogBodyClass` para `<FormDrawer>`. Mismo motivo de
 * existir; sólo cambia el ritmo vertical en escritorio, que en el panel lateral
 * es 14px (DS 4.8) y no los 18px de la modal.
 *
 * MANTENER EN SINCRONÍA con `SheetBody`/`DrawerBody`.
 */
export const responsiveDrawerBodyClass =
  "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:gap-[14px] md:p-[18px]";

/**
 * Cuerpo scrolleable. Es el reemplazo del `px-4 pb-4 md:px-0 md:pb-0` que cada
 * llamador repetía a mano para reconciliar el padding del drawer con el de la
 * modal: ahora el padding correcto de cada modo lo pone la primitiva.
 */
export function ResponsiveDialogBody({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerBody className={className} {...props} />;
  if (mode === "sheet") return <SheetBody className={className} {...props} />;
  return <DialogBody className={className} {...props} />;
}

/** Texto a la izquierda del pie; en móvil no se muestra (ver DialogFooterHint). */
export function ResponsiveDialogFooterHint({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return <DialogFooterHint className={className} {...props} />;
}

export function ResponsiveDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerFooter className={className} {...props} />;
  if (mode === "sheet") return <SheetFooter className={className} {...props} />;
  return <DialogFooter className={className} {...props} />;
}

export function ResponsiveDialogClose({
  ...props
}: React.ComponentProps<typeof DrawerClose>) {
  const { mode } = React.useContext(ResponsiveDialogModeContext);
  if (mode === "drawer") return <DrawerClose {...props} />;
  if (mode === "sheet") return <SheetClose {...props} />;
  return <DialogClose {...props} />;
}
